import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { ContentHeader, Field, Modal, MoneyInput, QueryStatus, useToast } from "@cx/ui";
import type { FinancialAccountView, ListResult, TransferView } from "@cx/contracts";
import { formatBRL, formatDateBR, todayInSaoPaulo } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";

/**
 * Transferências entre contas (RF-FIN-02): saída na origem e entrada no destino no mesmo grupo. O estorno é feito no
 * extrato de qualquer uma das contas (estorna os dois lados).
 */
export function TransfersPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const qc = useQueryClient();
  const perms = me.permissions as Permissions;
  const [creating, setCreating] = useState(false);
  const list = useInfiniteQuery({
    queryKey: ["fin-accounts", companyId, "transfers"],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<ListResult<TransferView>>(
        `/financial-accounts/transfers${pageParam ? `?cursor=${encodeURIComponent(pageParam)}` : ""}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];

  return (
    <>
      <ContentHeader
        title="Transferências"
        breadcrumb={["Finanças", "Transferências"]}
        actions={
          can(perms, "financial_accounts", "create") && (
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              Incluir transferência
            </button>
          )
        }
      />
      <div className="box card">
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && rows.length === 0 && (
          <p className="text-muted p-3 mb-0">Nenhuma transferência entre contas.</p>
        )}
        {rows.length > 0 && (
          <div className="table-responsive">
            <table className="table align-middle mb-0" aria-label="Transferências">
              <thead>
                <tr>
                  <th scope="col">Data</th>
                  <th scope="col">Origem</th>
                  <th scope="col">Destino</th>
                  <th scope="col">Histórico</th>
                  <th scope="col" className="text-end">
                    Valor
                  </th>
                  <th scope="col">Situação</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.groupId}>
                    <td>{formatDateBR(t.date)}</td>
                    <td>{t.fromAccountName}</td>
                    <td>{t.toAccountName}</td>
                    <td>{t.description ?? "—"}</td>
                    <td className="text-end">{formatBRL(t.amountCents)}</td>
                    <td>
                      {t.isReversal ? (
                        <span className="badge text-bg-secondary">Estorno</span>
                      ) : t.reversedAt ? (
                        <span className="badge text-bg-secondary">Estornada</span>
                      ) : (
                        <span className="badge text-bg-success">Efetivada</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {list.hasNextPage && (
          <div className="p-3 text-center">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={list.isFetchingNextPage}
              onClick={() => list.fetchNextPage()}
            >
              Carregar mais
            </button>
          </div>
        )}
      </div>
      {creating && (
        <TransferDialog
          onClose={() => setCreating(false)}
          onSaved={() => qc.invalidateQueries({ queryKey: ["fin-accounts", companyId] })}
        />
      )}
    </>
  );
}

function TransferDialog({ onClose, onSaved }: { onClose(): void; onSaved(): void }) {
  const { companyId, api } = useSession();
  const toast = useToast();
  const [open, setOpen] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [key] = useState(() => crypto.randomUUID());
  const [v, setV] = useState({
    fromAccountId: "",
    toAccountId: "",
    date: todayInSaoPaulo(),
    amountCents: null as number | null,
    description: "",
  });
  const accounts = useQuery({
    queryKey: ["fin-accounts", companyId],
    queryFn: () => api<{ data: FinancialAccountView[] }>("/financial-accounts"),
  });
  const active = accounts.data?.data.filter((a) => a.active) ?? [];
  const save = useMutation({
    mutationFn: () =>
      api("/financial-accounts/transfers", {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({ ...v, amountCents: v.amountCents ?? 0, description: v.description || null }),
      }),
    onSuccess: () => {
      toast("success", "Transferência incluída");
      onSaved();
      setOpen(false);
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      toast("danger", errorMessage(e));
    },
  });
  const select = (k: "fromAccountId" | "toAccountId") => (
    <select className="form-select" value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })}>
      <option value="">Selecione</option>
      {active.map((a) => (
        <option key={a.id} value={a.id}>
          {a.name}
        </option>
      ))}
    </select>
  );
  return (
    <Modal
      open={open}
      title="Nova transferência"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Transferir
          </button>
        </>
      }
    >
      <div className="row">
        <Field label="Conta de origem" required className="col-md-6" error={errors.fromAccountId}>
          {select("fromAccountId")}
        </Field>
        <Field label="Conta de destino" required className="col-md-6" error={errors.toAccountId}>
          {select("toAccountId")}
        </Field>
        <Field label="Data" required className="col-md-6" error={errors.date}>
          <input
            type="date"
            value={v.date}
            max={todayInSaoPaulo()}
            onChange={(e) => setV({ ...v, date: e.target.value })}
          />
        </Field>
        <Field label="Valor" required className="col-md-6" error={errors.amountCents}>
          <MoneyInput value={v.amountCents} onChange={(c) => setV({ ...v, amountCents: c })} />
        </Field>
        <Field label="Histórico" className="col-12" error={errors.description}>
          <input
            value={v.description}
            maxLength={200}
            onChange={(e) => setV({ ...v, description: e.target.value })}
          />
        </Field>
      </div>
    </Modal>
  );
}
